import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { AppleDeviceProfileResolverActions, selectProfileByType, selectProfileItemsByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  NgbAccordionBody,
  NgbAccordionButton,
  NgbAccordionCollapse,
  NgbAccordionDirective,
  NgbAccordionHeader,
  NgbAccordionItem,
  NgbAccordionToggle,
  NgbCollapse,
  NgbModal,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { AsyncResultComponent, PaneComponent, PanelComponent, YesNoIconComponent } from '@rocksoft/cdk';
import { AvailableLookupOsUpdate, AvailableOSUpdate, DeviceInformation, OSUpdateStatus } from '@rocksoft/mdm-sdk-v2';
import { isNotNullOrUndefined, isNotUndefined } from '@rocksoft/shared';
import { ToastService } from '@rocksoft/shared/toast';
import { merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { BytesToMbPipe } from '../../pipes/bytes-to-mb.pipe';
import { AppleCommandProgressService } from '../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../utilities/command-spinner/command-spinner.component';
import { RequestType } from '../../viewmodels/apple/commands/apple-command.model';
import { CommandProgress } from '../../viewmodels/apple/signalr/command-context.model';
import { InstallLookupOsUpdateModalComponent } from './install-lookup-os-update-modal/install-lookup-os-update-modal.component';
import { InstallOsUpdateModalComponent } from './install-os-update-model/install-os-update-modal.component';
@Component({
  selector: 'ml-mdm-apple-updates',
  templateUrl: './apple-updates.component.html',
  styleUrls: ['./apple-updates.component.scss'],
  imports: [
    PanelComponent,
    AsyncResultComponent,
    PaneComponent,
    CommandSpinnerComponent,
    FaIconComponent,
    NgbAccordionDirective,
    NgbAccordionItem,
    NgbAccordionHeader,
    NgbAccordionToggle,
    NgbAccordionButton,
    NgbCollapse,
    NgbAccordionCollapse,
    NgbAccordionBody,
    YesNoIconComponent,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    BytesToMbPipe,
  ],
})
export class AppleUpdatesComponent {
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly route = inject(ActivatedRoute);
  private readonly mdmService = inject(AppleMdmService);
  private readonly modalService = inject(NgbModal);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly udid = signal('');

  public readonly udid$: Observable<string> = toObservable(this.udid).pipe(
    filter(isNotNullOrUndefined),
    distinctUntilChanged()
  );

  public readonly apple$: Observable<DeviceInformation> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileByType('device-information')).pipe(filter(isNotUndefined)))
  );
  public readonly availableOsUpdates$: Observable<AvailableOSUpdate[]> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileItemsByType('mdm-available-os-update')))
  );
  public readonly availableLookupOsUpdates$: Observable<AvailableLookupOsUpdate[]> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileItemsByType('available-lookup-os-update')))
  );
  public readonly osUpdateState$: Observable<OSUpdateStatus[]> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileItemsByType('os-update-status')))
  );

  private readonly _updateCommandProgress = new Subject<CommandProgress>();
  readonly updateCommandProgress: Observable<CommandProgress> = this.udid$.pipe(
    switchMap((u) =>
      merge(this._updateCommandProgress, this.commandProgressService.commandsOfRequestType(u, 'AvailableOSUpdates'))
    )
  );
  private readonly _refreshStateCommandProgress = new Subject<CommandProgress>();
  readonly refreshStateCommandProgress: Observable<CommandProgress> = this.udid$.pipe(
    switchMap((u) =>
      merge(this._refreshStateCommandProgress, this.commandProgressService.commandsOfRequestType(u, 'OSUpdateStatus'))
    )
  );
  constructor() {
    effect((onCleanup) => {
      const params$ = this.route.parent?.params;
      if (!params$) {
        this.udid.set('');
        return;
      }
      const sub = params$
        .pipe(
          map((p: Params) => p.udid),
          filter(isNotNullOrUndefined),
          distinctUntilChanged()
        )
        .subscribe((_udid) => {
          this.udid.set(_udid);
        });

      onCleanup(() => {
        sub.unsubscribe();
      });
    });

    // When AvailableOSUpdates command is acknowledged
    effect((onCleanup) => {
      const _udid = this.udid();
      if (!_udid) return;
      const sub = this.updateCommandProgress
        .pipe(
          filter((progress) => progress.status === 'Acknowledged'),
          distinctUntilChanged()
        )
        .subscribe((progress) => {
          this.store.dispatch(
            AppleDeviceProfileResolverActions.profileRequested({
              udid: progress.udid,
              profileType: 'mdm-available-os-update',
            })
          );
        });

      onCleanup(() => {
        sub.unsubscribe();
      });
    });

    // When OSUpdateStatus command is acknowledged
    effect((onCleanup) => {
      const _udid = this.udid();
      if (!_udid) return;
      const sub = this.refreshStateCommandProgress
        .pipe(filter((progress) => progress.status === 'Acknowledged'))
        .subscribe((progress) => {
          this.store.dispatch(
            AppleDeviceProfileResolverActions.profileRequested({
              udid: progress.udid,
              profileType: 'os-update-status',
            })
          );
        });
      onCleanup(() => {
        sub.unsubscribe();
      });
    });
  }
  refresh() {
    const _udid = this.udid();
    if (!_udid) return;
    this._updateCommandProgress.next(this.createInitialCommandProgress(_udid, 'AvailableOSUpdates'));
    this.mdmService.getOsUpdates(_udid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
  refreshUpdateState() {
    const _udid = this.udid();
    if (!_udid) return;
    this._refreshStateCommandProgress.next(this.createInitialCommandProgress(_udid, 'OSUpdateStatus'));
    this.mdmService.getOsUpdateStates(_udid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
  private createInitialCommandProgress(udid: string, requestType: RequestType): CommandProgress {
    return {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType,
    };
  }
  openUpdatesModal(update: AvailableOSUpdate): void {
    const modal = this.modalService.open(InstallOsUpdateModalComponent, { scrollable: true, size: 'm' });
    const _udid = this.udid();
    if (!_udid) return;
    modal.componentInstance.udid = _udid;
    modal.componentInstance.update = update;
  }
  openLookupUpdatesModal(update: AvailableLookupOsUpdate): void {
    const modal = this.modalService.open(InstallLookupOsUpdateModalComponent, { scrollable: true, size: 'm' });
    const _udid = this.udid();
    if (!_udid) return;
    modal.componentInstance.udid = _udid;
    modal.componentInstance.update = update;
  }
}
